import * as React from 'react';
import styled from 'styled-components';
import { excludeKeys } from '../../utils';
import { INumericBoxProps, SourceOfChange } from './numeric-box-props';
import {
    splitNumber,
    numericUtility,
    removeSeparator,
    addSeparator,
    getSeparatorAndDecimalChar,
    getNumberAsString,
    getFallbackMinValue,
    SERVER_CULTURE,
    INumericUtilityProps,
    trimFractionWithDecimalPlaces,
    trimFractionWithMaxDecimalPlaces
} from './numeric-utlity';
import { Spinner } from './parts/spinner';
import { NumericField } from './parts/numeric-field';
import { isString } from '@agilepoint/utils-common';

interface INumericBoxState {
    fieldValue: string;
    isSeparatorRequired: boolean;
    isFocused: boolean;
}

class NumericBoxBase extends React.PureComponent<INumericBoxProps, INumericBoxState> {
    static defaultProps: Partial<INumericBoxProps> = {
        culture: 'en-US',
        max: Number.MAX_VALUE,
        min: -Number.MAX_VALUE
    };

    private numberFieldRef: React.RefObject<HTMLInputElement>;
    private prevChangedValue = '';

    static getDerivedStateFromProps(props: INumericBoxProps, state: INumericBoxState) {
        const { blockDecimal, culture, hideSeparator, value } = props;
        const { isSeparatorRequired } = state;

        if (value === undefined) {
            return null;
        }

        const [separator, decimalCharacter] = getSeparatorAndDecimalChar(culture);
        const valueWithoutSeparator = removeSeparator(value.toString(), separator);
        const valueFormatOptions = {
            withSeparator: (!hideSeparator && isSeparatorRequired),
            blockDecimal,
            culture,
            decimalCharacter
        };

        return {
            fieldValue: `${getNumberAsString(valueWithoutSeparator, valueFormatOptions)}${valueWithoutSeparator.endsWith(decimalCharacter) ? decimalCharacter : ''}`
        };
    }

    constructor(props: INumericBoxProps) {
        super(props);
        this.numberFieldRef = React.createRef<HTMLInputElement>();
        this.state = this.getInitializeState();
        if (props.componentRef) {
            props.componentRef.current = {
                getFieldValue: () => {
                    const { culture } = this.props;
                    const { current } = this.numberFieldRef;
                    if (!current) {
                        return '';
                    }
                    const value = this.removeSeparator(this.unwrapAdditionalContent(current.value));
                    return numericUtility.convertToCulture(value, culture, SERVER_CULTURE);
                },
                setValue: (newValue: string) => {
                    const { max, min, culture } = this.props;
                    const isInRange = numericUtility.isNumberInRange(newValue, max, min);
                    if (!isInRange) {
                        return;
                    }
                    this.updateValue(numericUtility.convertToCulture(newValue, SERVER_CULTURE, culture), true);
                }
            };
        }
    }

    render() {
        const { className, elementRef, hidden } = this.props;
        return hidden ? null : (
            <div className={className} ref={elementRef}>
                {this.renderTextBox()}
                {this.renderSpinner()}
            </div>
        );
    }

    componentDidMount() {
        const { blockDecimal, decimalPlaces, culture } = this.props;
        const { fieldValue } = this.state;
        const [, decimalCharacter] = getSeparatorAndDecimalChar(culture);
        const needsDecimalUpdate = (!blockDecimal && decimalPlaces !== undefined && fieldValue.slice(fieldValue.indexOf(decimalCharacter)).length !== decimalPlaces + 1);
        if (needsDecimalUpdate) {
            this.formatAndUpdateValue(fieldValue, 'autoUpdate');
        }
    }

    componentDidUpdate(prevProps: INumericBoxProps) {
        const { hideSeparator, blockDecimal } = this.props;
        if (hideSeparator === prevProps.hideSeparator && blockDecimal === prevProps.blockDecimal) {
            return;
        }
        const { fieldValue } = this.state;
        this.formatAndUpdateValue(fieldValue, 'autoUpdate');
    }

    private renderTextBox(): React.ReactElement {
        const { fieldValue } = this.state;
        const { culture, blockDecimal } = this.props;
        const numericBoxProps = excludeKeys(this.props,
            'type', 'defaultValue', 'componentRef', 'value', 'max', 'min', 'step', 'onValueChange', 'localization', 'elementRef', 'className');
        const [, decimalChar] = getSeparatorAndDecimalChar(culture);
        return (
            <NumericField {...numericBoxProps}
                value={this.wrapAdditionalContent(fieldValue)}
                decimalChar={blockDecimal ? '' : decimalChar}
                elementRef={this.numberFieldRef}
                onValueChange={this.onChange}
                changeValue={this.changeValue}
                onFocus={this.onFocus}
                onBlur={this.onBlur}
            />
        );
    }

    private renderSpinner(): React.ReactElement | null {
        const { hideSpinner, localization, disabled, error } = this.props;
        if (hideSpinner) {
            return null;
        }
        return <Spinner changeValue={this.changeValue} localization={localization} disabled={disabled} error={error} />;
    }

    private getInitializeState(): INumericBoxState {
        const { value, blockDecimal, maxDecimalPlaces, defaultValue, culture, hideSeparator, decimalPlaces } = this.props;
        const newValue = numericUtility.convertToCulture((value ? value.toString() : defaultValue ? defaultValue.toString() : ''), SERVER_CULTURE, culture);
        const [, decimalCharacter] = getSeparatorAndDecimalChar(culture);
        const formatOptions = { withSeparator: !hideSeparator, blockDecimal, culture, decimalCharacter, maxDecimalPlaces, decimalPlaces };
        const validValue = this.getValidValue(newValue);
        return {
            fieldValue: getNumberAsString(validValue, formatOptions),
            isSeparatorRequired: !hideSeparator,
            isFocused: false
        };
    }

    private onChange = (value: string) => {
        const numberField = this.numberFieldRef.current as HTMLInputElement;
        const cursorPosition = numberField.selectionEnd || 0;
        const newValue = this.getValidValue(value, true);
        this.updateValue(newValue, false);
        this.setCursorPosition(cursorPosition);
        this.postRender(newValue, 'input');
    }

    private onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        const { culture, maxDecimalPlaces, blockDecimal, decimalPlaces, onFocus } = this.props;
        const inputField = (this.numberFieldRef.current as HTMLInputElement);
        this.setState({
            isFocused: true
        });
        const value = this.unwrapAdditionalContent(inputField.value);
        const [separator, decimalCharacter] = getSeparatorAndDecimalChar(culture);
        let [wholeNumber, fraction = ''] = splitNumber(value, decimalCharacter);
        wholeNumber = removeSeparator(wholeNumber, separator);
        fraction = trimFractionWithDecimalPlaces(fraction, decimalPlaces);
        fraction = trimFractionWithMaxDecimalPlaces(fraction, maxDecimalPlaces);
        this.updateValue(blockDecimal ? wholeNumber : `${wholeNumber}${fraction.length > 0 ? decimalCharacter : ''}${fraction}`, false);
        if (onFocus !== undefined) {
            onFocus(e);
        }
    }

    private onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { onBlur } = this.props;
        const value = this.getValidValue((e.target as HTMLInputElement).value);
        this.setState({
            isFocused: false
        });
        this.formatAndUpdateValue(value, 'blur');
        if (onBlur !== undefined) {
            onBlur(e);
        }
    }

    private formatAndUpdateValue = (value: string, sourceOfChange: SourceOfChange) => {
        const { culture, maxDecimalPlaces, blockDecimal, decimalPlaces } = this.props;
        const [separator, decimalCharacter] = getSeparatorAndDecimalChar(culture);

        if (value === '-' || value === '.') {
            value = '';
        } else if (value.startsWith(decimalCharacter)) {
            value = `0${value}`;
        } else if (value.startsWith(`-${decimalCharacter}`)) {
            value = `${value[0]}0${value.substr(1, value.length)}`;
        } else if (value.endsWith(decimalCharacter)) {
            value = `${value}00`;
        }

        if (value.length) {
            let [wholeNumber, fraction = ''] = splitNumber(value, decimalCharacter);
            wholeNumber = removeSeparator(wholeNumber, separator);
            fraction = trimFractionWithDecimalPlaces(fraction, decimalPlaces);
            fraction = trimFractionWithMaxDecimalPlaces(fraction, maxDecimalPlaces);
            value = blockDecimal ? wholeNumber : `${wholeNumber}${(fraction.length > 0) ? `${decimalCharacter}${fraction}` : ''}`;
        }

        this.updateValue(value, true);

        if (removeSeparator(value, separator) === this.prevChangedValue) {
            return;
        }

        this.postRender(value, sourceOfChange);
    }

    private getValidValue(value: string, skipRangeCheck?: boolean) {
        const { culture, blockDecimal, maxDecimalPlaces, decimalPlaces, min, max } = this.props;
        const valueWithoutSeparator = this.removeSeparator(value);
        const [separator, decimalCharacter] = getSeparatorAndDecimalChar(culture);
        const isNegativeAllowed = min! < 0 || max! < 0;
        if (value === '' || (value === '-' && isNegativeAllowed) || (value === decimalCharacter && !blockDecimal)) {
            return value;
        }
        const numericUtilityProps: INumericUtilityProps = {
            blockDecimal, maxDecimalPlaces, separator, decimalCharacter, decimalPlaces
        };
        if (!skipRangeCheck) {
            numericUtilityProps.max = max;
            numericUtilityProps.min = min;
        }
        const isValid = numericUtility.isValid(valueWithoutSeparator, numericUtilityProps);
        return isValid ? valueWithoutSeparator : numericUtility.getFallBackValue(value, numericUtilityProps);
    }

    private changeValue = (increment: boolean, sourceOfChange: SourceOfChange) => {
        const { disabled } = this.props;
        if (disabled) {
            return;
        }
        const { step = 1, max, min, hideSeparator, culture, decimalPlaces, maxDecimalPlaces, blockDecimal } = this.props;
        const numberField = this.numberFieldRef.current as HTMLInputElement;
        const unwrappedValue = this.unwrapAdditionalContent(numberField.value);
        const value = numericUtility.convertToCulture(unwrappedValue, culture, SERVER_CULTURE);
        const cursorPosition = numberField.selectionEnd || 0;
        let newValue = this.removeSeparator(value, SERVER_CULTURE);
        newValue = newValue ? (increment ? String(Number(newValue) + step) : String(Number(newValue) - step)) : String(getFallbackMinValue(min, max));
        const isInRange = numericUtility.isNumberInRange(newValue, max, min);
        if (!isInRange) {
            return;
        }
        const [separator, decimalCharacter] = getSeparatorAndDecimalChar(SERVER_CULTURE);
        let [wholeNumber, fraction = ''] = splitNumber(newValue, decimalCharacter);
        wholeNumber = removeSeparator(wholeNumber, separator);
        fraction = trimFractionWithDecimalPlaces(fraction, decimalPlaces);
        fraction = trimFractionWithMaxDecimalPlaces(fraction, maxDecimalPlaces);
        newValue = blockDecimal ? wholeNumber : `${wholeNumber}${(fraction.length > 0) ? `${decimalCharacter}${fraction}` : ''}`;
        this.updateValue(newValue, !hideSeparator);
        this.setCursorPosition(cursorPosition);
        this.postRender(newValue, sourceOfChange);
    }

    private updateValue = (value: string, isSeparatorRequired?: boolean) => {
        this.setState({
            fieldValue: isSeparatorRequired ? this.addSeparator(value) : value,
            isSeparatorRequired: !!isSeparatorRequired
        });
    }

    private postRender(value: string, sourceOfChange: SourceOfChange) {
        const { onValueChange, culture } = this.props;
        if (onValueChange === undefined) {
            return;
        }
        value = this.removeSeparator(numericUtility.convertToCulture(value, culture, SERVER_CULTURE), SERVER_CULTURE);
        this.prevChangedValue = value;
        if (value.length) {
            const valueAsNumber = Number(value);
            onValueChange({ valueAsString: value, valueAsNumber, sourceOfChange });
            return;
        }
        onValueChange({ valueAsString: value, sourceOfChange });
    }

    private setCursorPosition(cursorPosition: number) {
        const numberField = this.numberFieldRef.current as HTMLInputElement;
        numberField.setSelectionRange(cursorPosition, cursorPosition);
    }

    private removeSeparator(value: string, culture?: string) {
        if (value.length === 0) {
            return value;
        }
        const { culture: cultureFromProps } = this.props;
        const [separator] = getSeparatorAndDecimalChar(culture ?? cultureFromProps);
        return removeSeparator(value, separator);
    }

    private addSeparator(value: string, culture?: string) {
        if (value.length === 0) {
            return value;
        }
        const { culture: cultureFromProps, hideSeparator } = this.props;
        const [, decimalCharacter] = getSeparatorAndDecimalChar(culture ?? cultureFromProps);
        const [wholeNumber, fraction = ''] = splitNumber(value, decimalCharacter);
        return hideSeparator ? value : addSeparator(wholeNumber, culture ?? cultureFromProps) + (fraction.length > 0 ? decimalCharacter + fraction : '');
    }

    private wrapAdditionalContent(value: string) {
        const { prefix, suffix } = this.props;
        const { isFocused } = this.state;

        if (isFocused) {
            return value;
        }

        if (isString(prefix)) {
            value = `${prefix}${value}`;
        }

        if (isString(suffix)) {
            value = `${value}${suffix}`;
        }

        return value;
    }

    private unwrapAdditionalContent(value: string) {
        const { prefix, suffix } = this.props;

        if (isString(prefix)) {
            value = value.replace(prefix, '');
        }

        if (isString(suffix)) {
            value = value.replace(suffix, '');
        }

        return value.trim();
    }
}

export const NumericBox = styled(NumericBoxBase).attrs(() => ({
    className: String('ap-numericBox-wrapper')
}))`
    min-width: 50px;
    position: relative;
    overflow: hidden;
    box-sizing: border-box;
    display: inline-block;
`;
